/**
 * 『ジオラマドラマ』 (Diorama Drama)
 * 作詞・作曲・編曲：廉 / MV監督：頃之介古論 / イラスト：結晶 / タイポグラフィ：岩佐知昴
 * Official MV: https://youtu.be/ftCqxjpxdc8
 *
 * ジェネラティブアート・コンセプト（1シーン完結・遊べる箱庭ジェネラティブ空間）:
 * 1. 特徴的なカラーパターン（Anemone I / II の二面性）:
 *    - 緑・黒・白 (#2bc866 / #141716 / #f5f8f6)
 *    - 緑・ピンク・白 (#2bc866 / #ec5598 / #fafbfc)
 *    - 赤・グレー・肌色 (#d41c24 / #888c94 / #f6e3c8)
 *    - 肌色・赤・黒 (#f4e7d0 / #d41c24 / #18191c)
 *    - Anemone I × II 二面性スプリット（赤×グレー×肌色 と 緑×ピンク×白 が画面を二分するモード）
 *    「再生成（Reroll）」ボタンを押すたびに、これらの象徴的なカラーパターンと長方形の生え方レイアウトが切り替わる。
 * 2. 不規則に生える長方形（Interactive Kinetic Rectangles）:
 *    - 画面の上下左右や境界線から不規則な幅・長さの長方形がリズムよくニョキニョキと伸縮・多段成長する。
 *    - マウスカーソルを近づけると、長方形がカーソルに反応して波打つように伸び上がり、サブブロックが分裂する。
 * 3. ぽつねんとした箱庭テレビと、シーンの断片が描かれたカードたち:
 *    - 中央の空間にぽつねんと置かれたレトロなブラウン管テレビ（箱庭）と、その周囲を立体的に浮遊・公転する「断片カード群」。
 *    - 各カードにはMVの断片（一輪のアネモネ、三日月と窓、向かい合う二面性シルエット、波形と鼓動、入れ子の箱庭テレビ）が描かれている。
 *    - カードや画面をクリックすると、その断片カードが箱庭テレビへと吸い込まれ、テレビのチャンネル（箱庭の中の景色）が切り替わるとともに、
 *      クリック地点から不規則な長方形が新たに生え広がる。
 */

export class DioramaGenerator {
  constructor(p) {
    this.p = p;
    this.time = 0;
    this.paletteIndex = -1; // 初回initで決定、再生成で切り替え
    this.layoutMode = 0;    // 0: 四辺フレーム成長, 1: 上下スカイライン対向成長, 2: 斜め二面性スプリット成長

    // MVの象徴的な5つのカラーパターン定義
    this.palettes = [
      {
        name: 'Green / Black / White (Anemone II Dark)',
        bg: [20, 23, 22],
        primary: [43, 200, 102],    // 鮮やかな緑
        secondary: [245, 248, 246], // 白
        accent: [236, 85, 152],     // ピンクアクセント
        ink: [245, 248, 246],
        cardBg: [24, 28, 27]
      },
      {
        name: 'Green / Pink / White (Anemone II Vivid)',
        bg: [246, 248, 246],
        primary: [43, 200, 102],    // 鮮やかな緑
        secondary: [236, 85, 152],  // 鮮やかなピンク
        accent: [22, 25, 24],       // 黒
        ink: [22, 25, 24],
        cardBg: [255, 255, 255]
      },
      {
        name: 'Red / Grey / Skin-Cream (Anemone I Classic)',
        bg: [136, 140, 148],        // スレートグレー
        primary: [212, 28, 36],     // 鮮血の赤
        secondary: [246, 227, 200], // 肌色（ウォームクリーム）
        accent: [24, 24, 28],       // 漆黒
        ink: [246, 227, 200],
        cardBg: [246, 227, 200]
      },
      {
        name: 'Skin-Cream / Red / Black (Anemone I Warm)',
        bg: [244, 231, 208],        // 肌色背景
        primary: [212, 28, 36],     // 赤
        secondary: [24, 25, 28],    // 黒
        accent: [136, 140, 148],    // グレー
        ink: [24, 25, 28],
        cardBg: [252, 244, 230]
      },
      {
        name: 'Duality Split (Anemone I Red-Skin × Anemone II Green-Pink)',
        isSplit: true,
        bg: [22, 24, 26],           // 左側: 黒〜グレー / 右側: 白〜クリーム
        bgRight: [246, 248, 246],
        primary: [212, 28, 36],     // 左: 赤
        secondary: [246, 227, 200], // 左: 肌色
        primaryRight: [43, 200, 102],   // 右: 緑
        secondaryRight: [236, 85, 152], // 右: ピンク
        accent: [43, 200, 102],
        ink: [246, 227, 200],
        cardBg: [30, 32, 36]
      }
    ];

    // 不規則に生える長方形群
    this.growingBars = [];
    // クリックで生えるインタラクティブ長方形バースト
    this.burstBars = [];
    // シーンの断片が描かれた浮遊カード群
    this.fragmentCards = [];
    // テレビへ吸い込まれる飛行中のカード
    this.flyingCards = [];

    // ぽつねんとした箱庭テレビの状態
    this.tvChannel = 0; // 0: 一輪のアネモネ, 1: 三日月と夜窓, 2: 二面性の横顔, 3: 鼓動オシロスコープ, 4: 無限入れ子テレビ
    this.tvStaticTimer = 0;
    this.tvBounce = 0;

    this.pointerX = 0;
    this.pointerY = 0;
    this.parallaxX = 0;
    this.parallaxY = 0;
    this.hoveredCardIndex = -1;
  }

  onReroll() {
    // 再生成ボタンを押すごとにカラーパターンとレイアウトを切り替える
    this.paletteIndex = (this.paletteIndex + 1) % this.palettes.length;
    this.layoutMode = (this.layoutMode + 1) % 3;
    this.tvChannel = (this.tvChannel + 1) % 5;
    this.tvStaticTimer = 1.0;
  }

  init(params) {
    const p = this.p;
    const w = p.width || 800;
    const h = p.height || 600;
    const density = params ? params.density : 0.5;

    if (this.paletteIndex < 0) {
      this.paletteIndex = Math.floor(p.random(this.palettes.length));
      this.layoutMode = Math.floor(p.random(3));
    }

    this.pointerX = w * 0.5;
    this.pointerY = h * 0.5;
    this.growingBars = [];
    this.burstBars = [];
    this.fragmentCards = [];
    this.flyingCards = [];

    // 1. 不規則に生える長方形群の生成
    this.buildGrowingRectangles(w, h, density);

    // 2. シーンの断片が描かれたカード群の生成（周囲を浮遊する10〜18枚）
    const cardCount = Math.floor(10 + density * 8);
    for (let i = 0; i < cardCount; i++) {
      const angle = (p.TWO_PI / cardCount) * i + p.random(-0.18, 0.18);
      const radiusX = p.random(0.24, 0.42) * w;
      const radiusY = p.random(0.22, 0.39) * h;
      this.fragmentCards.push({
        baseAngle: angle,
        orbitSpeed: p.random(0.08, 0.22) * (i % 2 === 0 ? 1 : -1),
        radiusX,
        radiusY,
        wRatio: p.random(0.10, 0.145),
        hRatio: p.random(0.075, 0.105),
        tilt: p.random(-0.28, 0.28),
        motifType: i % 5, // 0..4 のMV断片シーン
        colorRole: i % 3, // 0: primary, 1: secondary, 2: accent
        phase: p.random(p.TWO_PI),
        depth: p.random(0.6, 1.35),
        currentX: 0,
        currentY: 0
      });
    }
  }

  /**
   * 画面の四辺または分割軸から不規則に生える長方形群を生成
   */
  buildGrowingRectangles(w, h, density) {
    const p = this.p;
    const totalBars = Math.floor(44 + density * 36);

    for (let i = 0; i < totalBars; i++) {
      let side;
      if (this.layoutMode === 1) {
        // 上下スカイライン対向成長モード
        side = i % 2 === 0 ? 'bottom' : 'top';
      } else {
        // 四辺から不規則に生えるモード
        const sides = ['bottom', 'top', 'left', 'right'];
        side = sides[i % 4];
      }

      const posRatio = p.random(0.02, 0.98);
      const thickness = p.random(10, 34);
      const maxLenRatio = p.random(0.08, 0.34);

      this.growingBars.push({
        side,
        posRatio,
        thickness,
        maxLenRatio,
        curLenRatio: p.random(0.02, maxLenRatio),
        targetLenRatio: p.random(0.04, maxLenRatio),
        stepTimer: p.random(0, 60),
        stepInterval: p.random(22, 65),
        colorRole: i % 3, // 0: primary, 1: secondary, 2: accent
        style: i % 5,     // 0: 塗り潰し, 1: 二重枠線, 2: 先端キャップ付き, 3: ストライプ, 4: 十字マーク付き
        hasSubBlock: p.random() < 0.42,
        subOffset: p.random(0.3, 0.8),
        hoverBoost: 0
      });
    }
  }

  onPointerDown(x, y) {
    const p = this.p;
    const w = p.width;
    const h = p.height;
    const scale = Math.min(w, h) / 720;
    const tvX = w * 0.5 - this.parallaxX * 0.5;
    const tvY = h * 0.54 - this.parallaxY * 0.5;

    // 1. 浮遊カードをクリックしたか判定
    let clickedCard = null;
    for (let i = this.fragmentCards.length - 1; i >= 0; i--) {
      const c = this.fragmentCards[i];
      const cw = c.wRatio * w;
      const ch = c.hRatio * h;
      if (Math.abs(x - c.currentX) < cw * 0.6 && Math.abs(y - c.currentY) < ch * 0.6) {
        clickedCard = c;
        break;
      }
    }

    // 2. 箱庭テレビ自体をクリックしたか判定
    const clickedTV = Math.abs(x - tvX) < 95 * scale && Math.abs(y - tvY) < 80 * scale;

    if (clickedTV) {
      // テレビを直接クリックするとカラーパターン（Anemone I/II）とチャンネルが切り替わる！
      this.tvChannel = (this.tvChannel + 1) % 5;
      this.paletteIndex = (this.paletteIndex + 1) % this.palettes.length;
      this.tvStaticTimer = 1.0;
      this.tvBounce = 1.0;
    } else {
      // クリック位置（またはクリックしたカード）から箱庭テレビへ向かって断片カードが飛んでいく
      const motif = clickedCard ? clickedCard.motifType : Math.floor(p.random(5));
      const role = clickedCard ? clickedCard.colorRole : Math.floor(p.random(3));
      this.flyingCards.push({
        startX: clickedCard ? clickedCard.currentX : x,
        startY: clickedCard ? clickedCard.currentY : y,
        x: clickedCard ? clickedCard.currentX : x,
        y: clickedCard ? clickedCard.currentY : y,
        progress: 0,
        motifType: motif,
        colorRole: role,
        tilt: p.random(-0.4, 0.4)
      });
    }

    // 3. クリック地点から不規則な長方形クラスターがニョキッと生える！
    const count = 8;
    for (let i = 0; i < count; i++) {
      const isVert = i % 2 === 0;
      const dir = p.random() < 0.5 ? 1 : -1;
      this.burstBars.push({
        x: x + p.random(-45, 45) * scale,
        y: y + p.random(-45, 45) * scale,
        isVert,
        dir,
        thickness: p.random(8, 24) * scale,
        curLen: 0,
        maxLen: p.random(45, 135) * scale,
        colorRole: i % 3,
        alpha: 255,
        life: 1.0
      });
    }
    if (this.burstBars.length > 40) {
      this.burstBars.splice(0, this.burstBars.length - 40);
    }
  }

  onPointerMove(x, y) {
    this.pointerX = x;
    this.pointerY = y;
  }

  draw(params) {
    const p = this.p;
    const w = p.width;
    const h = p.height;
    const motion = params ? params.motion : 0.5;
    const glow = params ? params.glow : 0.6;
    const scale = Math.min(w, h) / 720;

    const dt = Math.min(p.deltaTime || 16.67, 50) / 16.67;
    const speedMult = 0.45 + motion * 1.2;
    this.time += 0.016 * dt * speedMult;

    if (this.tvStaticTimer > 0) {
      this.tvStaticTimer = Math.max(0, this.tvStaticTimer - 0.05 * dt);
    }
    if (this.tvBounce > 0) {
      this.tvBounce = Math.max(0, this.tvBounce - 0.07 * dt);
    }

    // パララックス補間
    const targetPx = ((this.pointerX || w * 0.5) / w - 0.5) * 24;
    const targetPy = ((this.pointerY || h * 0.5) / h - 0.5) * 24;
    this.parallaxX = p.lerp(this.parallaxX, targetPx, 0.08);
    this.parallaxY = p.lerp(this.parallaxY, targetPy, 0.08);

    const pal = this.palettes[this.paletteIndex];

    // =========================================================================
    // 1. 背景と箱庭の幾何学グリッド（Anemone I / II カラーパターン）
    // =========================================================================
    this.drawBackgroundAndGrid(w, h, scale, pal);

    // =========================================================================
    // 2. 不規則に生える長方形群（カーソル連動＆ステップ成長アニメーション）
    // =========================================================================
    this.updateAndDrawGrowingRectangles(w, h, scale, dt, speedMult, pal);

    // =========================================================================
    // 3. クリックで生えたインタラクティブ長方形バースト
    // =========================================================================
    this.updateAndDrawBurstBars(scale, dt, pal);

    // =========================================================================
    // 4. シーンの断片が描かれた浮遊カード群（背面レイヤー）
    // =========================================================================
    this.updateCardPositions(w, h, scale);
    this.drawFragmentCardsLayer(w, h, scale, pal, false);

    // =========================================================================
    // 5. 中央にぽつねんと置かれた「箱庭テレビ（CRT Television）」
    // =========================================================================
    const tvX = w * 0.5 - this.parallaxX * 0.5;
    const tvY = h * 0.54 - this.parallaxY * 0.5;
    this.drawDioramaTelevision(tvX, tvY, w, h, scale, pal, glow);

    // =========================================================================
    // 6. シーンの断片が描かれた浮遊カード群（前面レイヤー）＆ 飛行カード
    // =========================================================================
    this.drawFragmentCardsLayer(w, h, scale, pal, true);
    this.updateAndDrawFlyingCards(tvX, tvY, w, h, scale, dt, pal);

    // =========================================================================
    // 7. 控えめで洗練されたグラフィックデザイン装飾（コーナー表記）
    // =========================================================================
    this.drawMinimalGraphicAccents(w, h, scale, pal);
  }

  /**
   * 座標 (x, y) と colorRole に応じて、現在のパレット（または二面性スプリット）から色を取得
   */
  resolveColor(pal, role, x, y, w, h) {
    if (pal.isSplit) {
      // 斜め二面性スプリット判定
      const splitLine = w * 0.5 + (y - h * 0.5) * 0.28;
      const isRight = x > splitLine;
      if (role === 0) return isRight ? pal.primaryRight : pal.primary;
      if (role === 1) return isRight ? pal.secondaryRight : pal.secondary;
      return isRight ? pal.primary : pal.primaryRight;
    }
    if (role === 0) return pal.primary;
    if (role === 1) return pal.secondary;
    return pal.accent;
  }

  /**
   * 1. 背景と箱庭の設計グリッド・床面パースペクティブの描画
   */
  drawBackgroundAndGrid(w, h, scale, pal) {
    const p = this.p;
    p.background(pal.bg[0], pal.bg[1], pal.bg[2]);

    if (pal.isSplit) {
      // 二面性スプリットモード：右半分を白〜クリーム、左半分をダークに塗り分け
      p.push();
      p.noStroke();
      p.fill(pal.bgRight[0], pal.bgRight[1], pal.bgRight[2]);
      p.beginShape();
      p.vertex(w * 0.5 - h * 0.14, 0);
      p.vertex(w, 0);
      p.vertex(w, h);
      p.vertex(w * 0.5 + h * 0.14, h);
      p.endShape(p.CLOSE);
      p.pop();
    }

    // 箱庭（ジオラマ）の繊細なドット＆十字レジストレーションマーク格子
    p.push();
    const step = 64 * scale;
    for (let x = step; x < w; x += step) {
      for (let y = step; y < h; y += step) {
        const col = this.resolveColor(pal, 1, x, y, w, h);
        p.stroke(col[0], col[1], col[2], 38);
        p.strokeWeight(1);
        const gx = x - this.parallaxX * 0.2;
        const gy = y - this.parallaxY * 0.2;
        p.line(gx - 3, gy, gx + 3, gy);
        p.line(gx, gy - 3, gx, gy + 3);
      }
    }
    p.pop();
  }

  /**
   * 2. 不規則に生える長方形群の更新・描画（カーソルに反応してニョキニョキ伸びる！）
   */
  updateAndDrawGrowingRectangles(w, h, scale, dt, speedMult, pal) {
    const p = this.p;
    p.push();

    for (let i = 0; i < this.growingBars.length; i++) {
      const b = this.growingBars[i];

      // 一定間隔で目標の長さがカクッ、カクッと切り替わる（MV特有のステップ成長感）
      b.stepTimer += dt * speedMult;
      if (b.stepTimer >= b.stepInterval) {
        b.stepTimer = 0;
        b.stepInterval = p.random(20, 65);
        b.targetLenRatio = p.random(0.03, b.maxLenRatio);
      }

      // バーの基準座標を計算
      let bx = 0;
      let by = 0;
      let bw = 0;
      let bh = 0;
      let distToCursor = 9999;

      if (b.side === 'bottom') {
        bx = b.posRatio * w;
        distToCursor = Math.abs(this.pointerX - bx);
      } else if (b.side === 'top') {
        bx = b.posRatio * w;
        distToCursor = Math.abs(this.pointerX - bx);
      } else if (b.side === 'left') {
        by = b.posRatio * h;
        distToCursor = Math.abs(this.pointerY - by);
      } else {
        by = b.posRatio * h;
        distToCursor = Math.abs(this.pointerY - by);
      }

      // カーソルが近づくと長方形がニョキッと伸び上がるインタラクション
      const hoverTarget = distToCursor < 95 * scale
        ? p.map(distToCursor, 0, 95 * scale, 0.16, 0)
        : 0;
      b.hoverBoost = p.lerp(b.hoverBoost, hoverTarget, 0.14 * dt);

      b.curLenRatio = p.lerp(b.curLenRatio, b.targetLenRatio + b.hoverBoost, 0.12 * dt);

      const thick = b.thickness * scale;
      if (b.side === 'bottom') {
        const len = b.curLenRatio * h;
        bx = b.posRatio * w - thick * 0.5;
        by = h - len;
        bw = thick;
        bh = len;
      } else if (b.side === 'top') {
        const len = b.curLenRatio * h;
        bx = b.posRatio * w - thick * 0.5;
        by = 0;
        bw = thick;
        bh = len;
      } else if (b.side === 'left') {
        const len = b.curLenRatio * w;
        bx = 0;
        by = b.posRatio * h - thick * 0.5;
        bw = len;
        bh = thick;
      } else {
        const len = b.curLenRatio * w;
        bx = w - len;
        by = b.posRatio * h - thick * 0.5;
        bw = len;
        bh = thick;
      }

      const col = this.resolveColor(pal, b.colorRole, bx + bw * 0.5, by + bh * 0.5, w, h);
      const secCol = this.resolveColor(pal, (b.colorRole + 1) % 3, bx + bw * 0.5, by + bh * 0.5, w, h);

      // 長方形のスタイル別描画
      if (b.style === 0 || b.style === 2) {
        // 塗り潰し長方形
        p.noStroke();
        p.fill(col[0], col[1], col[2], 238);
        p.rect(bx, by, bw, bh);

        if (b.style === 2) {
          // 先端に別カラーのキャップ長方形が付く
          p.fill(secCol[0], secCol[1], secCol[2], 245);
          const cap = 7 * scale;
          if (b.side === 'bottom') p.rect(bx, by - cap, bw, cap);
          else if (b.side === 'top') p.rect(bx, by + bh, bw, cap);
          else if (b.side === 'left') p.rect(bx + bw, by, cap, bh);
          else p.rect(bx - cap, by, cap, bh);
        }
      } else if (b.style === 1) {
        // 枠線のみの中抜き長方形
        p.noFill();
        p.stroke(col[0], col[1], col[2], 235);
        p.strokeWeight(2.2 * scale);
        p.rect(bx + 1, by + 1, Math.max(2, bw - 2), Math.max(2, bh - 2));
      } else if (b.style === 3) {
        // 二重レイヤー長方形
        p.noStroke();
        p.fill(col[0], col[1], col[2], 230);
        p.rect(bx, by, bw, bh);
        p.fill(secCol[0], secCol[1], secCol[2], 235);
        p.rect(bx + bw * 0.22, by + bh * 0.22, bw * 0.56, bh * 0.56);
      } else {
        // 内部に小さな十字マークを持つ長方形
        p.noStroke();
        p.fill(col[0], col[1], col[2], 235);
        p.rect(bx, by, bw, bh);
        p.stroke(secCol[0], secCol[1], secCol[2], 220);
        p.strokeWeight(1.5 * scale);
        const mx = bx + bw * 0.5;
        const my = by + bh * 0.5;
        p.line(mx - 4 * scale, my, mx + 4 * scale, my);
        p.line(mx, my - 4 * scale, mx, my + 4 * scale);
      }

      // 連動して浮き上がる小さなサブ長方形（ジオラマドラマ特有の浮遊ブロック感）
      if (b.hasSubBlock) {
        p.noStroke();
        p.fill(secCol[0], secCol[1], secCol[2], 225);
        const gap = (12 + Math.sin(this.time * 3 + i) * 6) * scale;
        const subLen = 14 * scale;
        if (b.side === 'bottom') p.rect(bx, by - gap - subLen, bw, subLen);
        else if (b.side === 'top') p.rect(bx, by + bh + gap, bw, subLen);
        else if (b.side === 'left') p.rect(bx + bw + gap, by, subLen, bh);
        else p.rect(bx - gap - subLen, by, subLen, bh);
      }
    }
    p.pop();
  }

  /**
   * 3. クリック地点から生える長方形バーストの更新・描画
   */
  updateAndDrawBurstBars(scale, dt, pal) {
    const p = this.p;
    if (this.burstBars.length === 0) return;

    p.push();
    p.noStroke();
    for (let i = this.burstBars.length - 1; i >= 0; i--) {
      const bb = this.burstBars[i];
      bb.curLen = p.lerp(bb.curLen, bb.maxLen, 0.18 * dt);
      bb.life -= 0.022 * dt;
      if (bb.life <= 0) {
        this.burstBars.splice(i, 1);
        continue;
      }

      const col = this.resolveColor(pal, bb.colorRole, bb.x, bb.y, p.width, p.height);
      p.fill(col[0], col[1], col[2], bb.life * 240);

      if (bb.isVert) {
        const ry = bb.dir > 0 ? bb.y : bb.y - bb.curLen;
        p.rect(bb.x - bb.thickness * 0.5, ry, bb.thickness, bb.curLen);
      } else {
        const rx = bb.dir > 0 ? bb.x : bb.x - bb.curLen;
        p.rect(rx, bb.y - bb.thickness * 0.5, bb.curLen, bb.thickness);
      }
    }
    p.pop();
  }

  /**
   * 4. 浮遊する「シーンの断片カード」の座標更新とホバー判定
   */
  updateCardPositions(w, h, scale) {
    const p = this.p;
    this.hoveredCardIndex = -1;

    for (let i = 0; i < this.fragmentCards.length; i++) {
      const c = this.fragmentCards[i];
      const ang = c.baseAngle + this.time * c.orbitSpeed;
      const bobY = Math.sin(this.time * 1.8 + c.phase) * 10 * scale;

      c.currentX = w * 0.5 + Math.cos(ang) * c.radiusX - this.parallaxX * c.depth;
      c.currentY = h * 0.52 + Math.sin(ang) * c.radiusY + bobY - this.parallaxY * c.depth;

      const cw = c.wRatio * w;
      const ch = c.hRatio * h;
      if (Math.abs(this.pointerX - c.currentX) < cw * 0.55 && Math.abs(this.pointerY - c.currentY) < ch * 0.55) {
        this.hoveredCardIndex = i;
      }
    }
  }

  /**
   * シーンの断片が描かれたカード群の描画（背面／前面レイヤー分けで立体感を演出）
   */
  drawFragmentCardsLayer(w, h, scale, pal, isFrontLayer) {
    const p = this.p;

    for (let i = 0; i < this.fragmentCards.length; i++) {
      const c = this.fragmentCards[i];
      const isFront = c.depth >= 1.0;
      if (isFront !== isFrontLayer) continue;

      const isHovered = i === this.hoveredCardIndex;
      const cw = c.wRatio * w * (isHovered ? 1.16 : 1.0);
      const ch = c.hRatio * h * (isHovered ? 1.16 : 1.0);

      // ホバー中のカードからは箱庭テレビのアンテナへ点線シグナルが繋がる
      if (isHovered) {
        const tvX = w * 0.5 - this.parallaxX * 0.5;
        const tvY = h * 0.54 - this.parallaxY * 0.5 - 65 * scale;
        p.push();
        p.stroke(pal.primary[0], pal.primary[1], pal.primary[2], 190);
        p.strokeWeight(1.5 * scale);
        p.drawingContext.setLineDash([5 * scale, 5 * scale]);
        p.line(c.currentX, c.currentY, tvX, tvY);
        p.drawingContext.setLineDash([]);
        p.pop();
      }

      this.drawSingleFragmentCard(
        c.currentX,
        c.currentY,
        cw,
        ch,
        c.tilt + Math.sin(this.time * 1.4 + c.phase) * 0.05,
        c.motifType,
        c.colorRole,
        isHovered,
        w,
        h,
        scale,
        pal
      );
    }
  }

  /**
   * 断片カード1枚の描画（内部にMVの象徴的なワンシーン断片を描く）
   */
  drawSingleFragmentCard(x, y, cw, ch, tilt, motifType, colorRole, isHovered, w, h, scale, pal) {
    const p = this.p;
    const borderCol = this.resolveColor(pal, colorRole, x, y, w, h);
    const subCol = this.resolveColor(pal, (colorRole + 1) % 3, x, y, w, h);

    p.push();
    p.translate(x, y);
    p.rotate(tilt);
    p.rectMode(p.CENTER);

    // ドロップシャドウ
    p.noStroke();
    p.fill(0, 0, 0, isHovered ? 95 : 55);
    p.rect(5 * scale, 6 * scale, cw, ch, 3 * scale);

    // カード下地とフレーム
    p.fill(pal.cardBg[0], pal.cardBg[1], pal.cardBg[2], 248);
    p.stroke(borderCol[0], borderCol[1], borderCol[2], 245);
    p.strokeWeight((isHovered ? 3.2 : 2.0) * scale);
    p.rect(0, 0, cw, ch, 3 * scale);

    // カード内部のミニチュア・シーン描画
    this.drawMiniatureSceneContent(0, 0, cw * 0.86, ch * 0.82, motifType, borderCol, subCol, scale);

    p.pop();
  }

  /**
   * カード内部および箱庭テレビ画面内に描かれる5種類のMV断片シーン
   */
  drawMiniatureSceneContent(cx, cy, mw, mh, motifType, col1, col2, scale) {
    const p = this.p;
    p.push();
    p.translate(cx, cy);

    // ミニチュア内の小さな生える長方形（箱庭との統一感）
    p.noStroke();
    p.fill(col1[0], col1[1], col1[2], 90);
    for (let b = -2; b <= 2; b++) {
      const bh = (Math.sin(this.time * 2.5 + b * 1.3) * 0.2 + 0.25) * mh;
      p.rect(b * mw * 0.18 - mw * 0.05, mh * 0.5 - bh, mw * 0.08, bh);
    }

    if (motifType === 0) {
      // Scene A: 一輪のアネモネの花（Anemone Flower）
      p.noStroke();
      p.fill(col1[0], col1[1], col1[2], 240);
      const r = Math.min(mw, mh) * 0.28;
      for (let k = 0; k < 6; k++) {
        const a = (p.TWO_PI / 6) * k + this.time * 0.4;
        p.circle(Math.cos(a) * r * 0.55, Math.sin(a) * r * 0.55, r * 0.72);
      }
      p.fill(col2[0], col2[1], col2[2], 250);
      p.circle(0, 0, r * 0.48);
    } else if (motifType === 1) {
      // Scene B: 三日月と夜の窓枠（Moon & Night Window）
      p.noFill();
      p.stroke(col1[0], col1[1], col1[2], 235);
      p.strokeWeight(1.6 * scale);
      p.rectMode(p.CENTER);
      p.rect(0, 0, mw * 0.55, mh * 0.68);
      p.line(-mw * 0.275, 0, mw * 0.275, 0);
      p.line(0, -mh * 0.34, 0, mh * 0.34);
      p.noStroke();
      p.fill(col2[0], col2[1], col2[2], 240);
      p.circle(-mw * 0.10, -mh * 0.12, Math.min(mw, mh) * 0.24);
    } else if (motifType === 2) {
      // Scene C: Anemone I / II の向かい合う二面性シルエット
      p.noStroke();
      const r = Math.min(mw, mh) * 0.24;
      p.fill(col1[0], col1[1], col1[2], 235);
      p.circle(-mw * 0.16, -mh * 0.08, r);
      p.arc(-mw * 0.16, mh * 0.35, r * 1.6, r * 1.8, p.PI, p.TWO_PI);

      p.fill(col2[0], col2[1], col2[2], 235);
      p.circle(mw * 0.16, -mh * 0.08, r);
      p.arc(mw * 0.16, mh * 0.35, r * 1.6, r * 1.8, p.PI, p.TWO_PI);
    } else if (motifType === 3) {
      // Scene D: ジオラマ波形・鼓動オシロスコープ
      p.noFill();
      p.stroke(col1[0], col1[1], col1[2], 245);
      p.strokeWeight(2.0 * scale);
      p.beginShape();
      for (let x = -mw * 0.42; x <= mw * 0.42; x += 4) {
        const ny = Math.sin(x * 0.12 + this.time * 4.0) * mh * 0.25;
        p.vertex(x, ny);
      }
      p.endShape();
    } else {
      // Scene E: 入れ子の小さな箱庭テレビ（Recursive TV）
      p.rectMode(p.CENTER);
      p.noFill();
      p.stroke(col1[0], col1[1], col1[2], 240);
      p.strokeWeight(1.8 * scale);
      p.rect(0, mh * 0.05, mw * 0.56, mh * 0.52, 2 * scale);
      p.line(0, -mh * 0.21, -mw * 0.16, -mh * 0.38);
      p.line(0, -mh * 0.21, mw * 0.16, -mh * 0.38);
    }

    p.pop();
  }

  /**
   * 5. 中央にぽつねんと置かれた「箱庭テレビ（Miniature Diorama CRT TV）」の描画
   * （MVのオチであり象徴でもある、暗闇／空間にぽつんと置かれたテレビに箱庭が映る演出）
   */
  drawDioramaTelevision(tvX, tvY, w, h, scale, pal, glow) {
    const p = this.p;
    const bounceScale = 1.0 + Math.sin(this.tvBounce * p.PI) * 0.12;
    const tvW = 195 * scale * bounceScale;
    const tvH = 148 * scale * bounceScale;

    p.push();
    p.translate(tvX, tvY);

    // 1. テレビが置かれている箱庭の台座・影と放射パースライン
    p.noStroke();
    p.fill(0, 0, 0, 75);
    p.ellipse(0, tvH * 0.56, tvW * 1.25, 28 * scale);

    // テレビ画面から周囲へ漏れる柔らかな光彩（グロウ）
    const glowCol = pal.primary;
    for (let g = 4; g >= 1; g--) {
      p.fill(glowCol[0], glowCol[1], glowCol[2], (10 + glow * 14) * (5 - g) * 0.28);
      p.rectMode(p.CENTER);
      p.rect(0, 0, tvW + g * 28 * scale, tvH + g * 24 * scale, 18 * scale);
    }

    // 2. ラビットイヤー・アンテナ（V字アンテナ）
    p.stroke(pal.ink[0], pal.ink[1], pal.ink[2], 225);
    p.strokeWeight(2.4 * scale);
    p.line(-18 * scale, -tvH * 0.5, -58 * scale, -tvH * 0.88);
    p.line(18 * scale, -tvH * 0.5, 52 * scale, -tvH * 0.92);
    p.fill(pal.primary[0], pal.primary[1], pal.primary[2], 245);
    p.noStroke();
    p.circle(-58 * scale, -tvH * 0.88, 7 * scale);
    p.circle(52 * scale, -tvH * 0.92, 7 * scale);

    // 3. レトロテレビの筐体（外装ボックス）
    p.rectMode(p.CENTER);
    p.fill(26, 28, 32, 250);
    p.stroke(pal.secondary[0], pal.secondary[1], pal.secondary[2], 240);
    p.strokeWeight(3.0 * scale);
    p.rect(0, 0, tvW, tvH, 10 * scale);

    // 脚（小さなハの字スタンド）
    p.strokeWeight(4.0 * scale);
    p.line(-tvW * 0.34, tvH * 0.5, -tvW * 0.40, tvH * 0.60);
    p.line(tvW * 0.34, tvH * 0.5, tvW * 0.40, tvH * 0.60);

    // 4. ブラウン管スクリーン（左側の画面領域）
    const scrW = tvW * 0.72;
    const scrH = tvH * 0.78;
    const scrX = -tvW * 0.09;
    const scrY = 0;

    p.noStroke();
    p.fill(pal.cardBg[0], pal.cardBg[1], pal.cardBg[2], 250);
    p.rect(scrX, scrY, scrW, scrH, 8 * scale);

    // ブラウン管内部に映し出される「箱庭（ミニチュアのジオラマ世界）」
    if (this.tvStaticTimer > 0.1) {
      // チャンネル切り替え時の砂嵐ノイズ演出
      for (let s = 0; s < 28; s++) {
        p.fill(s % 2 === 0 ? pal.primary : pal.secondary);
        p.rect(
          scrX + p.random(-scrW * 0.42, scrW * 0.42),
          scrY + p.random(-scrH * 0.42, scrH * 0.42),
          p.random(12, 48) * scale,
          p.random(3, 8) * scale
        );
      }
    } else {
      this.drawMiniatureSceneContent(
        scrX,
        scrY,
        scrW * 0.92,
        scrH * 0.90,
        this.tvChannel,
        pal.primary,
        pal.secondary,
        scale * 1.15
      );
    }

    // ブラウン管の走査線とガラス枠
    p.stroke(0, 0, 0, 35);
    p.strokeWeight(1);
    for (let sy = -scrH * 0.45; sy <= scrH * 0.45; sy += 4 * scale) {
      p.line(scrX - scrW * 0.46, sy, scrX + scrW * 0.46, sy);
    }
    p.noFill();
    p.stroke(pal.primary[0], pal.primary[1], pal.primary[2], 235);
    p.strokeWeight(2.2 * scale);
    p.rect(scrX, scrY, scrW, scrH, 8 * scale);

    // 5. 右側のロータリーチャンネルつまみ（2個）とスピーカーグリル
    const panelX = tvW * 0.35;
    p.fill(pal.secondary[0], pal.secondary[1], pal.secondary[2], 240);
    p.noStroke();
    p.circle(panelX, -tvH * 0.22, 22 * scale);
    p.circle(panelX, -tvH * 0.02, 22 * scale);

    // つまみの回転インジケータ
    p.stroke(20, 22, 26, 240);
    p.strokeWeight(2.0 * scale);
    const dialAng = this.tvChannel * 1.2 + this.time * 0.5;
    p.line(panelX, -tvH * 0.22, panelX + Math.cos(dialAng) * 8 * scale, -tvH * 0.22 + Math.sin(dialAng) * 8 * scale);

    // スピーカーのスリット線
    p.stroke(pal.secondary[0], pal.secondary[1], pal.secondary[2], 160);
    p.strokeWeight(1.5 * scale);
    for (let k = 0; k < 4; k++) {
      const ly = tvH * (0.16 + k * 0.06);
      p.line(panelX - 9 * scale, ly, panelX + 9 * scale, ly);
    }

    p.pop();
  }

  /**
   * 6. クリック時に箱庭テレビへと吸い込まれる飛行カードの更新・描画
   */
  updateAndDrawFlyingCards(tvX, tvY, w, h, scale, dt, pal) {
    const p = this.p;
    for (let i = this.flyingCards.length - 1; i >= 0; i--) {
      const fc = this.flyingCards[i];
      fc.progress += 0.055 * dt;

      if (fc.progress >= 1.0) {
        // テレビに到着したらテレビのチャンネルがそのカードの絵柄に切り替わる！
        this.tvChannel = fc.motifType;
        this.tvStaticTimer = 0.65;
        this.tvBounce = 1.0;
        this.flyingCards.splice(i, 1);
        continue;
      }

      const ease = 1 - Math.pow(1 - fc.progress, 3);
      fc.x = p.lerp(fc.startX, tvX, ease);
      fc.y = p.lerp(fc.startY, tvY, ease) - Math.sin(fc.progress * p.PI) * 55 * scale;
      const shrink = p.lerp(1.0, 0.35, ease);

      this.drawSingleFragmentCard(
        fc.x,
        fc.y,
        w * 0.12 * shrink,
        h * 0.09 * shrink,
        fc.tilt + fc.progress * 3.2,
        fc.motifType,
        fc.colorRole,
        true,
        w,
        h,
        scale,
        pal
      );
    }
  }

  /**
   * 7. 画面隅の控えめなグラフィックデザイン装飾（タイトルとパレット名）
   */
  drawMinimalGraphicAccents(w, h, scale, pal) {
    const p = this.p;
    p.push();
    const x = w * 0.045;
    const y = h * 0.93;

    p.noStroke();
    p.fill(pal.primary[0], pal.primary[1], pal.primary[2], 220);
    p.rect(x, y - 6 * scale, 18 * scale, 12 * scale);

    p.fill(pal.ink[0], pal.ink[1], pal.ink[2], 190);
    p.textFont("'Noto Serif JP', 'Yu Mincho', serif");
    p.textAlign(p.LEFT, p.CENTER);
    p.textSize(11 * scale);
    p.text(`ジ オ ラ マ ド ラ マ   —   D I O R A M A   D R A M A`, x + 26 * scale, y);
    p.pop();
  }
}
